import React, {
  useState,
  useRef,
  useEffect,
  useCallback,
  useMemo,
} from 'react';
import { Select, Input } from 'antd';
import { Prompt, useHistory } from 'react-router-dom';
import alert from '@components/alert';
import Module from '@modules/projects/projectSetting/module';
import './systemModule.scss';
import { Button, PopAlert, Tabs, Tab } from 'appkit-react';
import { saveSystemTemplate } from '@service/apis';
import { useDispatch, useSelector } from 'react-redux';
import actions from '@flux/action';
import {
  systemModuleColumns,
  systemModuleDefaultColumns,
} from '@modules/systemSetting/systemModule/systemModuleColumns';
import {
  addIdToTemplateData,
  generateSystemModuleRequest,
  processSystemModuleResponse,
} from '@modules/systemSetting/systemModule/helper';
import { removeValueFromSearchParam, setURLSearchParam } from '@utils/history';
import { deepCopy } from '@utils/common';
import { API } from '@service';
import NavigationTiles from '../navigationTiles';
import GuidingPrinciplesSystemSetting from '../guidingPrinciples/guidingPrinciplesSystemSetting';
import {
  navigationTilesAddModalConfigOnSystemSetting,
  projectHomeAddModalConfigOnSystemSetting,
  projectModuleAddModalConfigOnSystemSetting,
} from '@model/settings/addModalConfig';
import LifecycleConfiguration from '@modules/systemSetting/systemModule/lifecycleConfiguration/lifecycleTemplate';
import modalConfig from '@modules/projects/projectDetail/lifeCycle/components/modal/modal.config';
import { EMPTY_NODE_ERROR } from '@modules/projects/projectDetail/lifeCycle/lifeCycle.module';
import ValueDrivers from '../valueDrivers';
import { useDebounce } from '@components';
import InstanceDefaultChatbotSettings, {
  buildDefaultChatbotConfigPayload,
  emptyDefaultChatbotFormState,
  mapFormValuesToDefaultChatbotState,
  normalizeDefaultChatbotFromApi,
  validateDefaultChatbotSlice,
} from '../instanceDefaultChatbotSettings';

const defaultAlertProps = {
  okText: 'yes',
  content: 'Do you want to save all the changes?',
  cancelText: 'No',
  async: true,
  noCloseIcon: true,
  maskClosable: false,
};

const popSuccess = () =>
  PopAlert({
    className: 'success-message-popover',
    oneLineDisplay: true,
    duration: 2000,
    status: 'success',
    message: 'SAVED SUCCESSFULLY',
  });

const PROJECT_TYPES = {
  CUSTOMIZE: 'Customize',
  INTEGRATION: 'Integration',
  DIVESTITURE: 'Divestiture',
};

const TitleSyncTarget = {
  PROJECT_HOME: 'PROJECT_HOME',
  LIFECYCLE: 'LIFECYCLE',
};

const lifecycleFinder = (i) =>
  i.identifier === 'lifecycle' || i.identifier === 'lifecycleDivestitures';

const Option = Select.Option;

const toServerSave = (data, projectInstance, currentTab) => {
  const result = {
    projectInstence: projectInstance,
    customizeModuleTemplate: data.customizeModuleTemplate,
    guidingPrincipleTemplate: {
      guidingPrincipleTemplateInfo: data.guidingPrincipleList,
      discussionGuideTemplateInfo: data.discussionGuideList,
    },
    integrationNavTemplate: {
      integrationNavigatorName: data.integrationName,
      integrationTabs: data.integrationTabs,
      displayMode: data.displayMode,
      type: projectInstance.id,
    },
    lifecycleTemplate: data.lifecycleTemplate,
    valueDriversTemplate: data.valueDriverData,
    defaultChatbotConfig: data.defaultChatbotConfig,
  };

  if (currentTab === '0') {
    if (
      Array.isArray(result.customizeModuleTemplate.customizeProjectHomeTmplInfo)
    ) {
      const lifecycleTitle =
        result.customizeModuleTemplate.customizeProjectHomeTmplInfo.find(
          lifecycleFinder,
        )?.section;
      if (lifecycleTitle) {
        result.lifecycleTemplate.lifecycleSectionInfo.sectionTitle =
          lifecycleTitle;
      }
    }
  } else if (currentTab === '2') {
    const lifecycleTitle =
      result.lifecycleTemplate.lifecycleSectionInfo.sectionTitle;
    if (lifecycleTitle) {
      const lifecycleOnProjectHome =
        result.customizeModuleTemplate.customizeProjectHomeTmplInfo.find(
          lifecycleFinder,
        );
      if (lifecycleOnProjectHome) {
        lifecycleOnProjectHome.section = lifecycleTitle;
      }
    }
  }

  return result;
};

const NEW_TYPE = 'New Type';

function SystemModule(props) {
  const [curType, setCurType] = useState(
    null,
    // Boolean(getValueFromSearchParam('isSellSide')?.isSellSide) || false,
  );
  const promptOpen = useRef(false);
  const history = useHistory();
  const moduleRef = useRef(null);
  const navRef = useRef(null);
  const guidingRef = useRef(null);
  const lifecycleRef = useRef(null);
  const valueDriverRef = useRef(null);
  const chatbotRef = useRef(null);
  const chatbotAgentsAvailableRef = useRef(false);
  const [defaultChatbotDraft, setDefaultChatbotDraft] = useState(() =>
    emptyDefaultChatbotFormState(),
  );
  const [chatbotFormResetKey, setChatbotFormResetKey] = useState(0);
  const [moduleData, setModuleData] = useState({});
  const [navData, setNavData] = useState({});
  const [guidingData, setGuidingData] = useState({});
  const [lifecycleData, setLifecycleData] = useState(null);
  const [curTab, setCurTab] = useState('0');
  const [projectTypes, setProjectTypes] = useState([]);
  const [instanceName, setInstanceName] = useState([]);
  const inputRef = useRef(null);
  const selectRef = useRef(null);
  const [lifeCycleScrollDetectionFlag, setLifeCycleScrollDetectionFlag] =
    useState(0);
  const [hasChange, setHasChange] = useState(false);

  const isOnGdTab = curTab === '3';
  const curProjectTypeInfo = projectTypes.find((e) => e.id === curType) || {};

  const syncLifecycleTitle = useCallback((title, syncTarget) => {
    if (!syncTarget) {
      return;
    }
    if (syncTarget === TitleSyncTarget.PROJECT_HOME) {
      const sectionForm = moduleRef.current?.getSectionForm();
      if (!sectionForm) {
        return;
      }
      const projectHomeSectionValues = sectionForm.getFieldsValue();
      if (Array.isArray(projectHomeSectionValues.projectHomeTmplt)) {
        const lifecycleSection =
          projectHomeSectionValues.projectHomeTmplt.find(lifecycleFinder);
        if (lifecycleSection) {
          lifecycleSection.section = title;
        }
        sectionForm.setFieldsValue(projectHomeSectionValues);
      }
    }
    if (
      syncTarget === TitleSyncTarget.LIFECYCLE &&
      lifecycleRef.current?.updateTitle
    ) {
      lifecycleRef.current.updateTitle(title);
    }
  }, []);

  const onTabChange = async (_, targetTab) => {
    if (curTab === '0') {
      const moduleForm = moduleRef.current?.getModuleForm();
      try {
        const sectionForm = moduleRef.current?.getSectionForm();
        await moduleForm.validateFields();
        const sectionValues = await sectionForm.validateFields();
        const lifecycleTitle =
          sectionValues?.projectHomeTmplt?.find?.(lifecycleFinder)?.section;
        if (lifecycleTitle) {
          syncLifecycleTitle(lifecycleTitle, TitleSyncTarget.LIFECYCLE);
        }
      } catch (e) {
        const error =
          e.errorFields && e.errorFields[0] && e.errorFields[0].errors[0];
        if (!e.errorFields) {
          return;
        }
        alert.warning(error);
        moduleForm.scrollToField(e.errorFields[0].name, { inline: 'start' });
        // scrollYSync();
        return;
      }
    } else if (curTab === '1') {
      const navForm = navRef.current?.getForm();
      try {
        await navForm.validateFields();
      } catch (e) {
        const error =
          e.errorFields && e.errorFields[0] && e.errorFields[0].errors[0];
        if (!e.errorFields) {
          return;
        }
        alert.warning(error);
        navForm.scrollToField(e.errorFields[0].name, { inline: 'start' });
        // scrollYSync();
        return;
      }
    } else if (curTab === '2') {
      try {
        const lifecycleData = lifecycleRef.current.validateFields();
        syncLifecycleTitle(
          lifecycleData?.lifecycleSectionInfo?.sectionTitle,
          TitleSyncTarget.PROJECT_HOME,
        );
      } catch (e) {
        if (e.message === EMPTY_NODE_ERROR) {
          alert.warning(modalConfig.emptyAlert);
          return;
        }
      }
    } else if (curTab === '3') {
      const guidingForm = guidingRef.current?.getForm();
      try {
        await guidingForm.validateFields();
      } catch (e) {
        const error =
          e.errorFields && e.errorFields[0] && e.errorFields[0].errors[0];
        if (!e.errorFields) {
          return;
        }
        alert.warning(error);
        guidingForm.scrollToField(e.errorFields[0].name, { inline: 'start' });
        // scrollYSync();
        return;
      }
    } else if (curTab === '4') {
      try {
        await valueDriverRef?.current?.validateFields();
      } catch (e) {
        return;
      }
    } else if (curTab === '5') {
      try {
        await chatbotRef.current?.validateFields();
      } catch (e) {
        const error =
          e.errorFields && e.errorFields[0] && e.errorFields[0].errors[0];
        if (!e.errorFields) {
          return;
        }
        alert.warning(error);
        return;
      }
    }
    setCurTab(targetTab);
    if (targetTab === '2') {
      setTimeout(() => {
        setLifeCycleScrollDetectionFlag((i) => i + 1);
      }, 0);
    }
  };

  const dispatch = useDispatch();
  const loading = useSelector(({ common }) => common.loading);

  const getProjectTemplates = async (type) => {
    dispatch(actions.changeLoading(true));
    const types = (await API.getAllProjectInstances()) || [];
    dispatch(actions.changeLoading(false));
    // const buyerSideId = (types.find((e) => e.type === 'Integration') || {}).id;
    setProjectTypes(types.filter((t) => !t.isDeleted));
    if (type) {
      setCurType(type);
      return;
    }
    if (Array.isArray(types)) {
      setCurType(types[0]?.id || '');
    }
  };

  useEffect(() => {
    const { type = PROJECT_TYPES.INTEGRATION } = curProjectTypeInfo;
    dispatch(actions.changeSettingCompanyInstance(type));
  }, [curProjectTypeInfo]);

  useEffect(() => {
    promptOpen.current = true;
    if (curType) {
      setURLSearchParam(history, { curType });
    } else {
      removeValueFromSearchParam(history, ['curType']);
    }
    promptOpen.current = false;
  }, [curType]);

  // 表单值变化的时候固定将标记位变为true
  const formChange = useDebounce(() => {
    setHasChange(true);
  }, 1000);

  const onTypeChange = async (type) => {
    if (hasChange) {
      return await getProjectTemplates(type);
    }
    alert.confirm({
      onOk: async () => {
        promptOpen.current = true;
        const { canNotSave } = (await saveForm()) || {};
        if (canNotSave) {
          promptOpen.current = false;
          return;
        }
        setTimeout(() => {
          getProjectTemplates(type);
        }, 0);
      },
      onCancel: async () => {
        promptOpen.current = true;
        setTimeout(() => {
          getProjectTemplates(type);
        }, 0);
      },
      ...defaultAlertProps,
    });
    // setCurType(type);
  };

  const getFormData = async () => {
    let formData, sectionFormData, navData, guidingData, lifecycleData;
    const moduleForm = moduleRef.current?.getModuleForm();
    const sectionForm = moduleRef.current?.getSectionForm();
    const navForm = navRef.current?.getForm();
    const guidingForm = guidingRef.current?.getForm();
    let valueDriverData = null;

    try {
      formData = await moduleForm.validateFields();
      sectionFormData = await sectionForm.validateFields();
      await navForm.validateFields();
      navData = navRef.current?.getData();
      guidingData = await guidingForm.validateFields();
    } catch (e) {
      const error =
        e.errorFields && e.errorFields[0] && e.errorFields[0].errors[0];
      console.log(e);
      if (!e.errorFields) {
        return;
      }
      alert.warning(error);
      moduleForm.scrollToField(e.errorFields[0].name, { inline: 'start' });
      // scrollYSync();

      throw new Error('fields invalid');
    }

    try {
      lifecycleData = lifecycleRef.current?.validateFields();
    } catch (e) {
      console.error('[saveForm] lifecycle validateFields failed:', e);
      if (e.message === EMPTY_NODE_ERROR) {
        alert.warning(modalConfig.emptyAlert);
      }
      throw new Error('fields invalid');
    }

    try {
      await valueDriverRef.current?.validateFields();
      valueDriverData = valueDriverRef.current?.getForm();
    } catch (e) {
      console.error('[saveForm] valueDriver validateFields failed:', e);
      if (e.message === EMPTY_NODE_ERROR) {
        setCurTab('4');
        alert.warning(modalConfig.emptyAlert);
      }
      throw new Error('fields invalid');
    }

    let chatbotSlice = defaultChatbotDraft;
    try {
      if (chatbotRef.current) {
        const fv = await chatbotRef.current.validateFields();
        chatbotSlice = mapFormValuesToDefaultChatbotState(fv);
        setDefaultChatbotDraft(chatbotSlice);
      } else {
        validateDefaultChatbotSlice(defaultChatbotDraft, {
          agentsAvailable: chatbotAgentsAvailableRef.current,
        });
      }
    } catch (e) {
      const error =
        e.errorFields && e.errorFields[0] && e.errorFields[0].errors[0];
      if (e.errorFields) {
        alert.warning(error || 'Invalid chatbot settings');
      } else if (typeof e?.message === 'string' && e.message) {
        alert.warning(e.message);
      }
      setCurTab('5');
      throw new Error('fields invalid');
    }

    const defaultChatbotConfig = buildDefaultChatbotConfigPayload(chatbotSlice);

    // eslint-disable-next-line consistent-return
    return toServerSave(
      {
        customizeModuleTemplate: generateSystemModuleRequest({
          ...formData,
          ...sectionFormData,
        }),
        ...navData,
        ...guidingData,
        lifecycleTemplate: lifecycleData,
        valueDriverData,
        defaultChatbotConfig,
      },
      {
        ...(curType !== NEW_TYPE
          ? projectTypes.find((e) => e.id === curType)
          : { type: PROJECT_TYPES.CUSTOMIZE }),
        instanceName,
      },
      curTab,
      // curType,
    );
  };

  // 判断section name是否有重复的
  function hasRepeat(arr) {
    const hash = {};
    let hashRepeat = false;
    (arr || []).forEach((item) => {
      const key = item.titleName;
      if (hash[key]) {
        return (hashRepeat = true);
      }
      hash[key] = true;
    });
    return hashRepeat;
  }

  const saveForm = async () => {
    if (instanceName.length === 0) {
      alert.warning('Type name is required!');
      promptOpen.current = false;
      return { canNotSave: true };
    }

    if (
      curProjectTypeInfo.instanceName !== instanceName &&
      projectTypes.some((e) => e.instanceName === instanceName)
    ) {
      alert.warning('Project type already exists!');
      promptOpen.current = false;
      return { canNotSave: true };
    }

    try {
      dispatch(actions.changeLoading(true));
      const values = deepCopy(await getFormData());
      if (!values) {
        dispatch(actions.changeLoading(false));
        return {};
      }
      // 处理multiple module的数据
      const moduleArr =
        values?.customizeModuleTemplate?.customizeModuleTmplInfo;
      let sectionNameHasEmpty = false; // 判断section name是否有空的标志位
      let sectionNameHasRepeated = false; // 判断section name是否有重复的标志位

      if (moduleArr) {
        moduleArr.forEach((item) => {
          if (item.type === 'multiSection') {
            item.display = (item?.multiSectionList || []).some(
              (item) => item.display,
            );
            item.internalPermTmplList = [
              {
                label: item.module,
                value: item.value,
              },
            ];
            if (
              !(item.multiSectionList || []).every((ite) =>
                Boolean(ite.titleName),
              )
            ) {
              console.log('有空的');
              sectionNameHasEmpty = true;
            }
            if (hasRepeat(item.multiSectionList)) {
              sectionNameHasRepeated = true;
            }
          }
        });
      }
      if (sectionNameHasEmpty) {
        console.log('有空的，不可以提交');
        alert.warning(
          'Please remember to enter section name in the multi-section module.',
        );
        return { canNotSave: true };
      } else if (sectionNameHasRepeated) {
        console.log('有重复的，不可以提交');
        alert.warning('Duplicate section name(s)');
        return { canNotSave: true };
      }
      await Promise.all([
        saveSystemTemplate(values),
        actions.saveValuedriverTemplate(curType, values.valueDriversTemplate),
      ]);
    } catch (e) {
      console.log(e);
      return { canNotSave: true };
    } finally {
      dispatch(actions.changeLoading(false));
    }
    popSuccess();
    promptOpen.current = true;
    return {};
  };
  const goBack = () => {
    props.history.replace('/settings');
  };

  const handleSubmitForm = async () => {
    const { canNotSave } = (await saveForm()) || {};
    if (canNotSave) {
      promptOpen.current = false;
      return { canNotSave };
    }
    setTimeout(() => {
      history.push('/settings');
      popSuccess();
    }, 10);
  };

  const handleDeleteTemplate = async () => {
    const onOk = async () => {
      if (!curProjectTypeInfo.id) {
        return null;
      }
      if (curType !== NEW_TYPE) {
        const res = await API.deleteSystemTemplate(curProjectTypeInfo.id);
        if (!res) {
          return;
        }
      }
      await getProjectTemplates();
    };
    // 如果没有数据改变的话不应该弹出confirm
    if (!hasChange) {
      return onOk();
    }
    alert.confirm({
      onOk,
      onCancel: () => {},
      ...defaultAlertProps,
      content: 'Do you want to delete this instance?',
    });
  };

  const handlePrompt = (location) => {
    // 如果没有数据改变的话不应该弹出confirm
    if (!hasChange) {
      return true;
    }
    if (!promptOpen.current) {
      alert.confirm({
        onOk: async () => {
          promptOpen.current = true;
          const { canNotSave } = (await handleSubmitForm()) || {};
          if (canNotSave) {
            promptOpen.current = false;
            return;
          }
          setTimeout(() => {
            history.push(location.pathname + location.search);
          }, 0);
        },
        onCancel: async () => {
          promptOpen.current = true;
          setTimeout(() => {
            history.push(location.pathname + location.search);
          }, 0);
        },
        ...defaultAlertProps,
      });
      return false;
    }
    return true;
  };

  const getTemplateById = async (id) => {
    if (typeof id !== 'string') {
      return;
    }
    setDefaultChatbotDraft(emptyDefaultChatbotFormState());
    setChatbotFormResetKey((k) => k + 1);
    const type = curProjectTypeInfo?.type || PROJECT_TYPES.CUSTOMIZE;
    dispatch(actions.changeLoading(true));
    let templateData;
    if (id !== NEW_TYPE) {
      templateData = await API.getTemplateById(id, type);
    } else {
      const defaultModuleData = await API.getDefaultTemplate();
      templateData = {
        customizeModuleTemplate: {
          customizeModuleTmplInfo:
            defaultModuleData?.deafultCustomzeModuleTemplate || [],
          customizeProjectHomeTmplInfo:
            defaultModuleData?.defaultCustomzeSectionTemplate || [],
        },
        guidingPrincipleTemplate: [],
        integrationNavTemplate: {
          name: 'Accelerating the Transition',
          integrationTabs: [],
        },
        lifecycleTemplate: addIdToTemplateData(
          defaultModuleData?.deafultLifecycle,
        ),
      };
    }
    const insightModule =
      templateData?.customizeModuleTemplate?.customizeModuleTmplInfo?.find(
        (i) => i.value === 'insight',
      );
    if (insightModule?.internalPermTmplList) {
      insightModule.internalPermTmplList =
        insightModule.internalPermTmplList.filter(
          (i) => i.label !== 'Insights',
        );
    }
    dispatch(actions.changeLoading(false));
    if (templateData?.customizeModuleTemplate) {
      setModuleData(
        processSystemModuleResponse(templateData.customizeModuleTemplate),
      );
    }
    if (templateData?.integrationNavTemplate) {
      setNavData(templateData.integrationNavTemplate);
    }
    if (templateData?.guidingPrincipleTemplate) {
      setGuidingData(templateData.guidingPrincipleTemplate);
    }
    if (templateData?.lifecycleTemplate) {
      setLifecycleData(templateData.lifecycleTemplate);
    }
    setDefaultChatbotDraft(
      normalizeDefaultChatbotFromApi(templateData?.defaultChatbotConfig),
    );
    setChatbotFormResetKey((k) => k + 1);
    // console.log({ templateData });
  };

  useEffect(() => {
    getTemplateById(curType).then(() => {
      setInstanceName(
        (projectTypes.find((e) => e.id === curType) || {}).instanceName || '',
      );
    });
  }, [curType]);

  useEffect(() => {
    getProjectTemplates();
  }, []);

  const addNewInstance = () => {
    selectRef?.current && selectRef.current.blur();
    setProjectTypes((types) => [
      ...types,
      {
        type: PROJECT_TYPES.CUSTOMIZE,
        instanceName: '',
        isDeleted: false,
        id: NEW_TYPE,
      },
    ]);
    setCurType('New Type');
    formChange();
    selectRef?.current && selectRef.current.blur();
    inputRef?.current && inputRef.current.focus();
  };

  const handleAddInstance = () => {
    const onOk = async () => {
      promptOpen.current = true;
      const { canNotSave } = (await saveForm()) || {};
      if (canNotSave) {
        promptOpen.current = false;
        return;
      }
      await getProjectTemplates();
      setTimeout(() => {
        addNewInstance();
      }, 0);
    };

    // 如果没有数据改变的话不应该弹出confirm
    if (!hasChange) {
      return onOk();
    }

    return alert.confirm({
      onOk,
      onCancel: addNewInstance,
      ...defaultAlertProps,
    });
  };

  const currentInstanceName = useMemo(
    () => projectTypes?.find((i) => i.id === curType)?.instanceName || '',
    [curType, projectTypes],
  );

  const onChatbotAgentsAvailable = useCallback((available) => {
    chatbotAgentsAvailableRef.current = Boolean(available);
  }, []);

  const onChatbotModelChange = useCallback((next) => {
    setDefaultChatbotDraft(next);
  }, []);

  return (
    <div className="manage-module-wrapper system-module projects-create-page-wrapper">
      <Prompt message={handlePrompt} />
      <header>
        <div className="present-text">Project Type</div>
        <Input
          className="instance-name-input"
          ref={inputRef}
          value={instanceName}
          onChange={(e) => {
            formChange();
            setInstanceName(e.target.value);
          }}
          maxLength={50}
        />
        <Select
          className="side-select"
          value={curType}
          onChange={onTypeChange}
          ref={selectRef}
          dropdownClassName="dd-select-dropdown project-instance-dropdown"
          dropdownStyle={{
            background: 'var(--inputBoxBg)',
          }}
          dropdownRender={(menu) => (
            <>
              {menu}
              <div
                type="text"
                onClick={handleAddInstance}
                style={{
                  color: '#E45C2B',
                  size: '0.875rem',
                  padding: '0.375rem 0.75rem',
                  cursor: 'pointer',
                  background: 'var(--inputBoxBg)',
                }}
              >
                <span className="appkiticon icon-plus-outline" /> Add New
                Project Instance
              </div>
              {/* </Space> */}
            </>
          )}
        >
          {projectTypes.map((e) => (
            <Option value={e.id} key={e.id}>
              {e.instanceName}
            </Option>
          ))}
        </Select>
      </header>
      <div className={`action-wrapper ${isOnGdTab ? 'margin-right-460' : ''}`}>
        {curProjectTypeInfo?.type === PROJECT_TYPES.CUSTOMIZE ? (
          <Button
            size="xl"
            kind="secondary"
            onClick={handleDeleteTemplate}
            isLoading={loading}
          >
            Delete
          </Button>
        ) : null}
        <Button size="xl" kind="secondary" onClick={goBack} isLoading={loading}>
          Cancel
        </Button>
        <Button size="xl" onClick={handleSubmitForm} isLoading={loading}>
          Save
        </Button>
      </div>
      <Tabs
        defaultValue="0"
        value={curTab}
        className="a-tab-item-padding-10"
        onChange={onTabChange}
      >
        <Tab value="0" label="System Module">
          <div className="manage-module-wrapper system-module-tab-content">
            <Module
              cRef={moduleRef}
              moduleFormData={moduleData}
              tabKey="modules"
              key={curType}
              columns={systemModuleColumns}
              defaultColumns={systemModuleDefaultColumns}
              ratioNeedFullSize={false}
              sectionAddModuleConfig={projectHomeAddModalConfigOnSystemSetting}
              addModuleConfig={projectModuleAddModalConfigOnSystemSetting}
              formChange={formChange}
            />
          </div>
        </Tab>
        <Tab value="1" label="Navigation Tiles">
          <NavigationTiles
            navigatorData={navData}
            cRef={navRef}
            addModuleConfig={navigationTilesAddModalConfigOnSystemSetting}
            formChange={formChange}
          />
        </Tab>
        <Tab value="2" label="Project Lifecycle">
          <LifecycleConfiguration
            data={lifecycleData}
            ref={lifecycleRef}
            instanceId={curType}
            instanceName={currentInstanceName}
            refreshScrollDetection={lifeCycleScrollDetectionFlag}
            formChange={formChange}
          />
        </Tab>
        <Tab value="3" label="Guiding Principles">
          <GuidingPrinciplesSystemSetting
            {...guidingData}
            cRef={guidingRef}
            companyInstance={curProjectTypeInfo.instanceName || ''}
            formChange={formChange}
          />
        </Tab>
        <Tab value="4" label="Value Drivers">
          <ValueDrivers
            cRef={valueDriverRef}
            instanceId={curType}
            formChange={formChange}
          />
        </Tab>
        <Tab value="5" label="Chatbot settings">
          <InstanceDefaultChatbotSettings
            ref={chatbotRef}
            key={curType}
            model={defaultChatbotDraft}
            formResetKey={chatbotFormResetKey}
            onModelChange={onChatbotModelChange}
            formChange={formChange}
            onAgentsAvailableChange={onChatbotAgentsAvailable}
          />
        </Tab>
      </Tabs>
    </div>
  );
}

export default SystemModule;
